import { dateFormatter, formatToDecimals } from '@lib/helpers/formatting';
import React, { useEffect, useRef, useState } from 'react';
// import { Row, Form, Col } from 'react-bootstrap';
import { InvoiceDataType } from './SummaryInvoiceModal';
import { ResumeChecklistProjectType } from './ResumeChecklistCellRenderer';
import StopLightWithMessage from './StopLightWithMessage';
import SummaryInfoList from './SummaryInfoList';
import { Accordion } from 'react-bootstrap';
import SummaryInfoStatus from './SummaryInfoStatus';
import SummaryInvoiceModalInfoPositionDetails from './SummaryInvoiceModalInfoPositionDetails';

interface SummaryInvoiceModalInfoProps {
	invoiceData: InvoiceDataType;
	project: ResumeChecklistProjectType;
	// sections?: Record<string, string | boolean>;
}

const SummaryInvoiceModalInfo: React.FC<SummaryInvoiceModalInfoProps> = ({
	invoiceData,
	project,
	// , sections
}) => {
	const sectionsToDisplay = [
		{
			key: 'ammissibilita',
			label: 'Ammissibilità',
			color: invoiceData.sections?.['ammissibilita'],
		},
		{
			key: 'fattura',
			label: 'Conformità',
			color: invoiceData.sections?.['fattura'],
		},
		{
			key: 'inerenza',
			label: 'Inerenza',
			color: invoiceData.sections?.['inerenza'],
		},
		{ key: 'quietanza', label: 'Quietanza', color: invoiceData.sections?.['quietanza'] },
		{ key: 'dicituraDiAnnullamento', label: 'Timbratura', color: invoiceData.sections?.['dicituraDiAnnullamento'] },
	];

	const items = [
		{
			label: 'Nome Fornitore',
			value: invoiceData.supplierName ?? 'N.D.',
		},
		{
			label: 'Codice Fornitore',
			value: invoiceData.supplierCode ?? 'N.D.',
		},
		{
			label: 'P.Iva Fornitore',
			value: invoiceData.supplierVat ?? 'N.D.',
		},
		{
			label: 'Nr. Fattura',
			value: invoiceData?.number ?? 'N.D.',
		},
		{
			label: 'Data Fattura',
			value: invoiceData?.date ? dateFormatter(invoiceData?.date) : 'N.D.',
		},
		{
			label: 'ID Fattura',
			value: invoiceData?.rpCode ?? 'N.D.',
		},
		{
			label: 'Nr. Contratto Dedicato',
			value: invoiceData?.contract?.po === false ? invoiceData?.contract?.number : 'N.D.',
		},
		{
			label: 'Nr. Attingimento',
			value: invoiceData?.contract?.po === true ? invoiceData?.contract?.number : 'N.D.',
		},
		{
			label: invoiceData?.project?.cupType === 'I' ? 'ID Progetto' : 'CUP',
			value: invoiceData?.project?.cup ?? invoiceData?.stream?.project?.cup ?? 'N.D.',
		},
		...(project.streamConfigurationType === 'WP'
			? []
			: [
					{
						label: 'Nr. Procedura',
						value: invoiceData?.tenderCig ? invoiceData?.tenderCig?.tender?.tenderNumber : 'N.D.',
					},
					{
						label: 'Nr. CIG',
						value: invoiceData?.tenderCig?.cig ?? 'N.D.',
					},
				]),
		{
			label: 'Nr. Contratto Quadro',
			value:
				invoiceData?.contract?.po === true && invoiceData?.contract?.frameworkContract
					? invoiceData?.contract?.frameworkContract.number
					: 'N.D.',
		},
		{
			label: 'Imponibile (€)',
			value: invoiceData?.taxableAmount ? formatToDecimals(invoiceData?.taxableAmount) : 'N.D.',
		},
		{
			label: 'Iva (€)',
			value: invoiceData?.vatAmount ? formatToDecimals(invoiceData?.vatAmount) : 'N.D.',
		},
		{
			label: 'Totale Fattura (€)',
			value: invoiceData?.totalAmount ? formatToDecimals(invoiceData?.totalAmount) : 'N.D.',
		},
		{
			label: project.streamConfigurationType === 'WP' ? 'WP' : 'Stream',
			value: invoiceData?.stream?.streamId ? `${invoiceData?.stream?.streamId} - ${invoiceData?.stream?.name}` : 'N.D.',
		},
		...(project.streamConfigurationType === 'WP'
			? []
			: [
					{
						label: 'Classifica Regis',
						value: invoiceData?.regisTaxonomy ?? 'N.D.',
					},
					{
						label: 'Classifica interna',
						value: invoiceData?.internalTaxonomy ?? 'N.D.',
					},
				]),
		{
			label: 'Fattispecie RSI',
			value: invoiceData?.rdiTypeCode ?? 'N.D.',
		},
		{
			label: 'Avvio Progetto',
			value: invoiceData?.project?.startDate ? dateFormatter(invoiceData?.project?.startDate) : 'N.D.',
		},
		...(invoiceData?.project?.cupType !== 'I'
			? [
					{
						label: 'CUP Pagamento',
						value: invoiceData?.paymentCup ?? 'N.D.',
					},
					...(project.streamConfigurationType === 'WP'
						? []
						: [
								{
									label: 'CIG Pagamento',
									value: invoiceData?.paymentCig ?? 'N.D.',
								},
							]),
					{
						label: 'CRO Pagamento',
						value: invoiceData?.payment?.cro ?? 'N.D.',
					},
				]
			: []),
	];

	const containerRef = useRef<HTMLDivElement>(null);
	const [containerSize, setContainerSize] = useState<string>();
	useEffect(() => {
		if (!containerRef.current) return;

		const ITEM_WIDTH = 250; // Approximate width of each item including margin/padding
		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;

			if (width >= 3 * ITEM_WIDTH) {
				setContainerSize('large');
			} else if (width >= 2 * ITEM_WIDTH) {
				setContainerSize('medium');
			} else {
				setContainerSize('small');
			}
		});

		observer.observe(containerRef.current);

		return () => observer.disconnect();
	}, []);

	return (
		<div className={`summary_info-container summary_info-container-${containerSize}`} ref={containerRef}>
			{project.streamConfigurationType === 'STREAM' && !!invoiceData.sections ? (
				containerSize === 'large' ? (
					<div
						style={{
							minHeight: '160px',
							display: 'grid',
							gridTemplateColumns: '1fr 2fr',
							marginBottom: '1rem',
						}}
					>
						<div
							style={{
								backgroundColor: '#EDF0F7',
								padding: '1rem',
								borderTopLeftRadius: '8px',
								borderBottomLeftRadius: '8px',
								display: 'grid',
								gap: '10px',
								alignItems: 'center',
							}}
						>
							<p
								style={{
									marginTop: 'auto',
									marginBottom: 0,
									fontWeight: 700,
									fontSize: '16px',
								}}
							>
								Stato della Fattura
							</p>
							<div
								style={{
									marginBottom: 'auto',
								}}
							>
								<StopLightWithMessage checklistColor={invoiceData.checkliststatus?.color} />
							</div>
						</div>
						<div
							style={{
								padding: '0.5rem 1rem',
								display: 'grid',
								gridTemplateColumns: 'repeat(3, 1fr)',
								borderWidth: '1px 1px 1px 0px',
								borderStyle: 'solid',
								borderColor: '#E3E7EB',
								borderTopRightRadius: '8px',
								borderBottomRightRadius: '8px',
							}}
						>
							{sectionsToDisplay.map((item) => (
								<div key={item.key}>
									<p
										style={{
											marginBottom: '0.5rem',
											fontWeight: 700,
											fontSize: '14px',
										}}
									>
										{item.label}
									</p>
									<StopLightWithMessage
										checklistColor={`${item.color}`}
										messageStyles={{
											fontSize: '12px',
											lineHeight: '16px',
										}}
									/>
								</div>
							))}
						</div>
					</div>
				) : (
					<Accordion className="invoice-modal_info-accordion">
						<Accordion.Item eventKey="0">
							<Accordion.Header
								style={{
									backgroundColor: '#EDF0F7',
									padding: 0,
									borderRadius: '8px',
									display: 'grid',
									gap: '10px',
									alignItems: 'center',
									textAlign: 'left',
									textTransform: 'none',
									width: '100%',
								}}
							>
								Stato della Fattura
								<div style={{ marginLeft: containerSize === 'medium' ? 'auto' : undefined }}>
									<StopLightWithMessage checklistColor={invoiceData.checkliststatus?.color} messageStyles={{ fontWeight: 500 }} />
								</div>
							</Accordion.Header>
							<Accordion.Body
								style={{
									display: 'grid',
									gap: '20px',
									gridTemplateColumns: containerSize === 'medium' ? '1fr 1fr' : '1fr',
								}}
							>
								{sectionsToDisplay.map((item) => (
									<div key={item.key}>
										<p
											style={{
												marginBottom: '0.5rem',
												fontWeight: 700,
												fontSize: '14px',
											}}
										>
											{item.label}
										</p>
										<StopLightWithMessage
											checklistColor={`${item.color}`}
											messageStyles={{
												fontSize: '12px',
												lineHeight: '16px',
											}}
										/>
									</div>
								))}
							</Accordion.Body>
						</Accordion.Item>
					</Accordion>
					// <div>
					// 	<Button
					// 		onClick={() => {
					// 			setAreSectionsVisible((prevAreSectionsVisible) => !prevAreSectionsVisible);
					// 		}}
					// 		className="btn custom-ghost-button"
					// 		style={{
					// 			backgroundColor: '#EDF0F7',
					// 			padding: '1rem',
					// 			borderRadius: '8px',
					// 			display: 'grid',
					// 			gap: '10px',
					// 			alignItems: 'center',
					// 			textAlign: 'left',
					// 			textTransform: 'none',
					// 			width: '100%',
					// 		}}
					// 	>
					// 		<p
					// 			style={{
					// 				marginTop: 'auto',
					// 				marginBottom: 0,
					// 				fontWeight: 700,
					// 				fontSize: '16px',
					// 			}}
					// 		>
					// 			Stato della Fattura
					// 		</p>
					// 		<div
					// 			style={{
					// 				marginBottom: 'auto',
					// 			}}
					// 		>
					// 			<StopLightWithMessage checklistColor={invoiceData.checkliststatus?.color} messageStyles={{ fontWeight: 500 }} />
					// 		</div>
					// 	</Button>
					// 	<div
					// 		style={{
					// 			height: areSectionsVisible ? '100%' : '0%',
					// 			overflow: 'hidden',
					// 			transition: 'height 0.3s ease',
					// 		}}
					// 	>
					// 		sdf
					// 	</div>
					// </div>
				)
			) : null}
			{project.streamConfigurationType === 'WP' || !invoiceData.sections ? (
				<SummaryInfoStatus entityLabel="Stato della fattura" checklistColor={invoiceData.checkliststatus?.color} />
			) : null}

			<div>
				<h4>Informazioni</h4>
				<SummaryInfoList items={items} />
			</div>

			<SummaryInvoiceModalInfoPositionDetails invoiceData={invoiceData} />

			{/* <Row className="mt-3">
				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Nome Fornitore</Form.Label>
					<Form.Text as="p">{invoiceData?.supplierName ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Codice Fornitore</Form.Label>
					<Form.Text as="p">{invoiceData?.supplierCode ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">P.Iva Fornitore</Form.Label>
					<Form.Text as="p">{invoiceData?.supplierVat ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Data Fattura</Form.Label>
					<Form.Text as="p">{invoiceData?.date ? dateFormatter(invoiceData?.date) : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Nr. Fattura</Form.Label>
					<Form.Text as="p">{invoiceData?.number ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">ID Fattura</Form.Label>
					<Form.Text as="p">{invoiceData?.rpCode ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="">Nr. Contratto Dedicato</Form.Label>
					<Form.Text as="p">{invoiceData?.contract?.po === false ? invoiceData?.contract?.number : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Nr. Attingimento</Form.Label>
					<Form.Text as="p">{invoiceData?.contract?.po === true ? invoiceData?.contract?.number : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem" style={{ overflowWrap: 'break-word' }}>
					<Form.Label className="mb-3">{invoiceData?.project?.cupType === 'I' ? 'ID' : 'CUP'} Progetto</Form.Label>
					<Form.Text as="p">{invoiceData?.project?.cup ?? invoiceData?.stream?.project?.cup ?? 'N.D.'}</Form.Text>
				</Form.Group>

				{project.streamConfigurationType === 'WP' ? null : (
					<Form.Group as={Col} className="col-4 px-4 mb-2rem">
						<Form.Label className="mb-3">Nr. Procedura</Form.Label>
						<Form.Text as="p">{invoiceData?.tenderCig ? invoiceData?.tenderCig?.tender?.tenderNumber : 'N.D.'}</Form.Text>
					</Form.Group>
				)}

				{project.streamConfigurationType === 'WP' ? null : (
					<Form.Group as={Col} className="col-4 px-4 mb-2rem">
						<Form.Label className="mb-3">CIG</Form.Label>
						<Form.Text as="p">{invoiceData?.tenderCig?.cig ?? 'N.D.'}</Form.Text>
					</Form.Group>
				)}

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Nr. Contratto Quadro</Form.Label>
					<Form.Text as="p">
						{invoiceData?.contract?.po === true && invoiceData?.contract?.frameworkContract
							? invoiceData?.contract?.frameworkContract.number
							: 'N.D.'}
					</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Imponibile (€)</Form.Label>
					<Form.Text as="p">{invoiceData?.taxableAmount ? formatToDecimals(invoiceData?.taxableAmount) : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Iva (€)</Form.Label>
					<Form.Text as="p">{invoiceData?.vatAmount ? formatToDecimals(invoiceData?.vatAmount) : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Totale Fattura (€)</Form.Label>
					<Form.Text as="p">{invoiceData?.totalAmount ? formatToDecimals(invoiceData?.totalAmount) : 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">{project.streamConfigurationType === 'WP' ? 'WP' : 'Stream'}</Form.Label>
					<Form.Text as="p">
						{invoiceData?.stream?.streamId ? `${invoiceData?.stream?.streamId} - ${invoiceData?.stream?.name}` : 'N.D.'}
					</Form.Text>
				</Form.Group>

				{project.streamConfigurationType === 'WP' ? null : (
					<Form.Group as={Col} className="col-4 px-4 mb-2rem">
						<Form.Label className="mb-3">Classifica Regis</Form.Label>
						<Form.Text as="p">{invoiceData?.regisTaxonomy ?? 'N.D.'}</Form.Text>
					</Form.Group>
				)}

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Classifica interna</Form.Label>
					<Form.Text as="p">{invoiceData?.internalTaxonomy ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Fattispecie RSI</Form.Label>
					<Form.Text as="p">{invoiceData?.rdiTypeCode ?? 'N.D.'}</Form.Text>
				</Form.Group>

				<Form.Group as={Col} className="col-4 px-4 mb-2rem">
					<Form.Label className="mb-3">Avvio Progetto</Form.Label>
					<Form.Text as="p">{invoiceData?.project?.startDate ? dateFormatter(invoiceData?.project?.startDate) : 'N.D.'}</Form.Text>
				</Form.Group>


			</Row> */}
			{/* {invoiceData?.project?.cupType !== 'I' && (
				<Row className="mb-2rem">
					<h2>Dettaglio Pagamento</h2>

					<Form.Group as={Col} className="col-4 px-4">
						<Form.Label className="mb-3">CUP Pagamento</Form.Label>
						<Form.Text as="p">{invoiceData?.paymentCup ?? 'N.D.'}</Form.Text>
					</Form.Group>

					{project.streamConfigurationType === 'WP' ? null : (
						<Form.Group as={Col} className="col-4 px-4">
							<Form.Label className="mb-3">CIG Pagamento</Form.Label>
							<Form.Text as="p">{invoiceData?.paymentCig ?? 'N.D.'}</Form.Text>
						</Form.Group>
					)}

					<Form.Group as={Col} className="col-4 px-4">
						<Form.Label className="mb-3">CRO Pagamento</Form.Label>
						<Form.Text as="p">{invoiceData?.payment?.cro ?? 'N.D.'}</Form.Text>
					</Form.Group>
				</Row>
			)} */}
			{/* 
                                            <Form.Group as={Col} className='col-4 px-4 mb-2rem'>
                                               <Form.Label className='mb-3'>Ritenuta D'Acconto (€)</Form.Label>
                                               <Form.Text as='p'>
                                                  {invoice?.withholding ? formatToDecimals(invoice?.withholding) : 'N.D.'}
                                               </Form.Text>
                                            </Form.Group>
  
                                            <Form.Group as={Col} className='col-4 px-4 mb-2rem'>
                                               <Form.Label className='mb-3'>Contributi Cassa (€)</Form.Label>
                                               <Form.Text as='p'>
                                                  {invoice?.pensionFundContribution
                                                     ? formatToDecimals(invoice?.pensionFundContribution)
                                                     : 'N.D.'}
                                               </Form.Text>
                                            </Form.Group>
  
                                            <Form.Group as={Col} className='col-4 px-4 mb-2rem'>
                                               <Form.Label className='mb-3'>Spese di Trasporto (€)</Form.Label>
                                               <Form.Text as='p'>
                                                  {invoice?.shippingFee ? formatToDecimals(invoice?.shippingFee) : 'N.D.'}
                                               </Form.Text>
                                            </Form.Group>
                                            */}
		</div>
	);
};

export default SummaryInvoiceModalInfo;
